import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Typography
} from "@material-ui/core";
import MemoryOutlinedIcon from "@material-ui/icons/MemoryOutlined";
import HelpOutlineOutlinedIcon from "@material-ui/icons/HelpOutlineOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import api from "../../services/api";
import toastError from "../../errors/toastError";

/**
 * Controles de IA no header do ticket:
 * - Toggle "Bot/IA ativo para este cliente" (apenas se ticket.clientId).
 * - Botao "Diagnosticar IA" → abre dialog com /ai-flows/diagnostics/:ticketId.
 */
const TicketAiControls = ({ ticket }) => {
  const clientId = ticket?.clientId || ticket?.contact?.clientId || null;
  const ticketId = ticket?.id;

  const [aiSetting, setAiSetting] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diag, setDiag] = useState(null);

  const fetchSetting = useCallback(async () => {
    if (!clientId) {
      setAiSetting(null);
      return;
    }
    try {
      const { data } = await api.get(`/clients/${clientId}/ai-settings`);
      setAiSetting(data);
    } catch (err) {
      // silencioso — pode nao ter permissao
    }
  }, [clientId]);

  useEffect(() => {
    fetchSetting();
  }, [fetchSetting]);

  const handleToggleBot = async () => {
    if (!clientId || !aiSetting) return;
    setToggling(true);
    const next = !(aiSetting.botEnabled !== false);
    try {
      const { data } = await api.put(`/clients/${clientId}/ai-settings`, {
        botEnabled: next
      });
      setAiSetting(data);
      toast.success(
        next ? "Bot/IA ativado para o cliente." : "Bot/IA desativado para o cliente."
      );
    } catch (err) {
      toastError(err);
    } finally {
      setToggling(false);
    }
  };

  const handleOpenDiagnostics = async () => {
    if (!ticketId) return;
    setDiagOpen(true);
    setDiag(null);
    setDiagLoading(true);
    try {
      const { data } = await api.get(`/ai-flows/diagnostics/${ticketId}`);
      setDiag(data);
    } catch (err) {
      toastError(err);
      setDiagOpen(false);
    } finally {
      setDiagLoading(false);
    }
  };

  const botEnabled = aiSetting ? aiSetting.botEnabled !== false : null;

  return (
    <>
      {clientId && (
        <Tooltip
          title={
            botEnabled === null
              ? "Carregando..."
              : botEnabled
              ? "Bot/IA ativo para este cliente (clique para desativar)"
              : "Bot/IA desativado para este cliente (clique para ativar)"
          }
        >
          <span>
            <IconButton
              size="small"
              onClick={handleToggleBot}
              disabled={toggling || !aiSetting}
            >
              <MemoryOutlinedIcon
                fontSize="small"
                style={{
                  color:
                    botEnabled === null
                      ? "#bbb"
                      : botEnabled
                      ? "#6a1b9a"
                      : "#bbb",
                  opacity: botEnabled ? 1 : 0.45
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      )}

      <Tooltip title="Diagnosticar IA neste atendimento">
        <span>
          <IconButton
            size="small"
            onClick={handleOpenDiagnostics}
            disabled={!ticketId}
          >
            <HelpOutlineOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog
        open={diagOpen}
        onClose={() => setDiagOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Diagnostico de IA — Ticket #{ticketId}</DialogTitle>
        <DialogContent>
          {diagLoading ? (
            <div style={{ textAlign: "center", padding: 32 }}>
              <CircularProgress />
            </div>
          ) : !diag ? null : (
            <>
              <div style={{ marginBottom: 12 }}>
                <Chip
                  size="small"
                  label={
                    diag.willRunAi
                      ? "Engine roda neste ticket"
                      : "Engine NAO roda — veja os checks abaixo"
                  }
                  style={{
                    background: diag.willRunAi ? "#e8f5e9" : "#ffebee",
                    color: diag.willRunAi ? "#1b5e20" : "#b71c1c",
                    fontWeight: 600
                  }}
                />
              </div>
              <Divider />
              {Object.entries(diag.checks).map(([key, check]) => (
                <div
                  key={key}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    padding: "8px 0",
                    borderBottom: "1px solid #f0f0f0"
                  }}
                >
                  {check.ok ? (
                    <CheckCircleOutlineIcon
                      fontSize="small"
                      style={{ color: "#2e7d32", marginTop: 2 }}
                    />
                  ) : (
                    <HighlightOffIcon
                      fontSize="small"
                      style={{ color: "#c62828", marginTop: 2 }}
                    />
                  )}
                  <div>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {key}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {check.message}
                    </Typography>
                    {check.detail ? (
                      <pre
                        style={{
                          fontSize: 11,
                          background: "#f5f5f5",
                          padding: 4,
                          margin: "4px 0 0",
                          borderRadius: 4,
                          overflow: "auto"
                        }}
                      >
                        {JSON.stringify(check.detail, null, 2)}
                      </pre>
                    ) : null}
                  </div>
                </div>
              ))}
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ display: "block", marginTop: 12 }}
              >
                Esta consulta nao dispara IA — apenas le o estado atual do
                gating. Procure o primeiro item vermelho de cima pra baixo:
                e o motivo do bot nao ter respondido.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiagOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TicketAiControls;
